import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDashboard } from 'api/clubApi';
import {
  getUpcomingMeetData,
  getRecentMeetData,
} from 'services/admin/ClubDashboardService';

import Loading from 'components/common/Loading';
import MetaTags from 'components/common/MetaTags';
import DashboardLayout from 'components/dashboard/DashboardLayout';
import ClubDashboardComponent from 'components/admin/ClubDashboardComponent';
import { convertDateTime } from 'utils/time';

const ClubDashboard = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const [fetchingDashboard, setFetchingDashboard] = useState(true);

  const [meetData, setMeetData] = useState([]);
  const [upcomingMeets, setUpcomingMeets] = useState([]);
  const [clubRecords, setClubRecords] = useState([]);
  const [seasonName, setSeasonName] = useState([]);

  // Decides what the meet cards call a meet's type (utils/clubFeatures.js). A plain value, so it can be a
  // dependency of the fetch below without refetching when the club object is merely replaced.
  const hasMultiClubMeets = selectedClub?.hasMultiClubMeets;

  const fetchClubDashboard = useCallback(async () => {
    if (!selectedClub?.clubID) return;
    try {
      const data = await getDashboard(selectedClub.clubID);
      // Always replace state from the response. Switching to a club with no
      // upcoming/recent meets or records must CLEAR the previous club's data —
      // guarding each setter on `.length` left it stale (the component stays
      // mounted across a club switch, so old state is not reset on its own).
      setMeetData(
        data?.recentMeets?.length ? getRecentMeetData(data.recentMeets) : []
      );
      setUpcomingMeets(
        data?.upcomingMeets?.length
          ? getUpcomingMeetData(data.upcomingMeets, { hasMultiClubMeets })
          : []
      );
      setClubRecords(
        data?.clubRecords?.length
          ? data.clubRecords.map((record) => ({
              ...record,
              achievedAt: convertDateTime(record.achievedAt).date,
            }))
          : []
      );
      setSeasonName(data?.seasonName ?? null);
    } catch (err) {
      console.log('[ClubDashboard] Fetch clubDashboard error: ', err);
      // Don't leave another club's data on screen if the fetch fails.
      setMeetData([]);
      setUpcomingMeets([]);
      setClubRecords([]);
      setSeasonName(null);
    }
  }, [selectedClub?.clubID, hasMultiClubMeets]);

  useEffect(() => {
    // Show the loading state while (re)fetching — including on a club switch —
    // so the new club's dashboard never flashes the previous club's content.
    setFetchingDashboard(true);
    fetchClubDashboard().then(() => {
      setFetchingDashboard(false);
    });
  }, [fetchClubDashboard]);

  return fetchingDashboard ? (
    <>
      <Loading isLoadingAdminPage={true} />
    </>
  ) : (
    <>
      <MetaTags title="SplitFast | Home" />
      <DashboardLayout
        variant="hero"
        clubName={selectedClub?.clubName}
        seasonName={seasonName}
      >
        <ClubDashboardComponent
          selectedClub={selectedClub}
          clubRecords={clubRecords}
          recentMeets={meetData}
          upcomingMeets={upcomingMeets}
        ></ClubDashboardComponent>
      </DashboardLayout>
    </>
  );
};

export default ClubDashboard;
